import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { allowNoteTypeDelete } from '../../graphql/controller-notes/allow-note-type-delete';
import { createNoteType } from '../../graphql/controller-notes/create-note-type';
import { deleteNoteType } from '../../graphql/controller-notes/delete-note-type';
import { getNoteTypes } from '../../graphql/controller-notes/get-note-types';
import { updateNoteType } from '../../graphql/controller-notes/update-note-type';
import { NoteTypeInput } from '../../model/admin/note-type-input';
import { BaseAppSyncService } from '../base-app-sync.service';
import { NoteTypeBuilderService } from '../builder/note-type-builder.service';
import {
  CategoryObject,
  NoteType,
  NoteTypeConnection,
  NoteTypeSortOrQuery,
} from '../../model/crm/note-type';
import { getControllerNoteCategories } from '../../graphql/controller-notes/get-controller-note-categories';
import { queryForNoteTypes } from 'src/app/graphql/controller-notes/query-for-note-types';

@Injectable({
  providedIn: 'root',
})
export class NoteTypeApiService extends BaseAppSyncService {
  private _noteTypeBuilderService = inject(NoteTypeBuilderService);

  getNoteTypes(): Observable<NoteType[]> {
    return this.query(
      getNoteTypes,
      {},
      (items: any[]) =>
        items.map((i) => this._noteTypeBuilderService.buildNoteType(i)),
      'getNoteTypes',
      'cache-first'
    );
  }

  createNoteType(input: NoteTypeInput | null): Observable<NoteType> {
    return this.mutate(
      createNoteType,
      {
        name: input?.name,
        enabled: input?.enabled,
        categories: input?.categories,
        aors: input?.aors.map((aor) => aor.id),
      },
      (i: any) => this._noteTypeBuilderService.buildNoteType(i),
      'createNoteType'
    );
  }

  updateNoteType(id: string, changes: NoteTypeInput | null): Observable<any> {
    return this.mutate(
      updateNoteType,
      {
        id: id,
        name: changes?.name,
        enabled: changes?.enabled,
        categories: changes?.categories,
        aors: changes?.aors.map((aor) => aor.id),
      },
      (i: any) => this._noteTypeBuilderService.buildNoteType(i),
      'updateNoteType'
    );
  }

  allowNoteTypeDelete(id: string): Observable<boolean> {
    return this.query(
      allowNoteTypeDelete,
      { id },
      (i: any) => i.value,
      'allowNoteTypeDelete'
    );
  }

  deleteNoteType(id: string): Observable<boolean> {
    return this.mutate(deleteNoteType, { id }, (i: any) => i, 'deleteNoteType');
  }

  getControllerNoteCategories(): Observable<CategoryObject[]> {
    return this.query(
      getControllerNoteCategories,
      {},
      (items: any[]) => items,
      'getControllerNoteCategories',
      'cache-first'
    );
  }

  queryForNoteTypes(
    page: number,
    limit: number,
    query: NoteTypeSortOrQuery,
    sort: NoteTypeSortOrQuery
  ): Observable<NoteTypeConnection> {
    return this.query(
      queryForNoteTypes,
      { page, limit, query, sort },
      (response: any) => {
        return {
          items: response.items.map((value: any) =>
            this._noteTypeBuilderService.buildNoteType(value)
          ),
          totalRecords: response.totalRecords,
        };
      },
      'queryForNoteTypes'
    );
  }
}
